/**
 * Réglages du joueur : le son, les crédits, et la remise à zéro.
 *
 * ══ Trois manques que l'audit du 17 août a relevés d'un bloc ═════════════════
 *
 * **Aucun réglage de son.** `SoundEngine` porte pourtant `sfxVolume`, `musicVolume` et
 * `musicEnabled` depuis l'étape 4a, avec leur persistance — rien ne les exposait. Un jeu
 * qui joue un son à chaque pièce produite et qu'on ne peut pas baisser se ferme.
 *
 * **Aucun écran Crédits.** Les licences Synty et Kenney l'exigent, `CREDITS.md` le
 * rappelle, et CLAUDE.md le porte en dette depuis la Phase 1. Une obligation
 * contractuelle qui n'existe que dans un fichier du dépôt n'est pas remplie.
 *
 * **Aucune remise à zéro.** Le joueur ne pouvait pas recommencer autrement qu'en vidant
 * son navigateur — ce qui emporte aussi sa session et donc son adresse publique.
 *
 * ══ Pourquoi ces trois-là dans le même module ═══════════════════════════════
 *
 * Ils partagent leur hôte — la boîte de réglages — et leur nature : ce sont des choses
 * qu'on fait UNE fois, pas des gestes de jeu. Les disperser dans trois fichiers aurait
 * triplé le câblage pour trois lignes chacune.
 */

import { APP_VERSION } from '@/config/changelog';
import { t } from '@/i18n';
import type { SoundEngine } from '@/audio/sound';
import { createButton } from './components';
import { appendDecision, createModal } from './components/overlays';

export interface PreferencesHandle {
  /** Lignes à monter dans la boîte de réglages, dans l'ordre. */
  readonly rows: readonly HTMLElement[];
  destroy(): void;
}

export interface PreferencesOptions {
  readonly sound: SoundEngine;
  /** Efface la partie et recharge. Fourni par la couche qui possède le stockage. */
  readonly onReset: () => void;
}

/**
 * Une ligne de réglage : un libellé à gauche, un contrôle à droite.
 *
 * `name` n'est pas décoratif — il pose `data-setting`, la seule accroche stable sur ces
 * lignes. Sans lui, une sonde ne peut viser que « le premier bouton de la troisième
 * ligne », c'est-à-dire faire une **supposition sur l'ordre du DOM** (règle 22) : le jour
 * où un réglage s'intercale, elle clique ailleurs et échoue vingt lignes plus loin sur un
 * symptôme muet. C'est exactement ce qui s'était produit dans la boutique.
 */
function row(name: string, label: string, control: HTMLElement): HTMLElement {
  const el = document.createElement('div');
  el.className = 'settings-row';
  el.dataset.setting = name;

  const text = document.createElement('span');
  text.className = 'settings-row__label';
  text.textContent = label;

  el.append(text, control);
  return el;
}

export function createPreferences(options: PreferencesOptions): PreferencesHandle {
  /* ── Le son ──────────────────────────────────────────────────────────────── */

  /*
    Un curseur, pas une bascule.
    Couper le son est une décision ; le baisser en est une autre, bien plus fréquente —
    on joue à un idle en fond, à côté d'autre chose. Une bascule oblige à choisir entre
    « fort » et « rien », ce qui pousse au « rien ».
  */
  const sfx = document.createElement('input');
  sfx.type = 'range';
  sfx.className = 'settings-range';
  sfx.min = '0';
  sfx.max = '100';
  sfx.step = '5';
  sfx.value = String(Math.round(options.sound.getSettings().sfxVolume * 100));
  sfx.setAttribute('aria-label', t('settings.sound.effects'));
  sfx.addEventListener('input', () => {
    options.sound.setSettings({ sfxVolume: Number(sfx.value) / 100 });
  });

  /* ── Les crédits ─────────────────────────────────────────────────────────── */

  const creditsModal = createModal({ title: t('settings.credits.title'), icon: 'shield' });
  document.body.append(creditsModal.el);

  /*
    Les mentions sont écrites EN DUR ici, et c'est volontaire.

    Elles ne se traduisent pas — « Environment and building models by Synty Studios » est
    la formule exacte que la licence demande, et la traduire la rendrait non conforme.
    Les faire passer par `t()` inviterait précisément à cette erreur.
  */
  const list = document.createElement('ul');
  list.className = 'credits-list';
  for (const line of [
    'Environment and building models by Synty Studios — POLYGON MINI',
    'Sound effects by Kenney (kenney.nl) — CC0',
    'Nunito by Vernon Adams, Cyreal & Jacques Le Bailly — SIL Open Font License',
    'PixiJS · Vite · TypeScript · Supabase',
  ]) {
    const item = document.createElement('li');
    item.textContent = line;
    list.append(item);
  }

  const version = document.createElement('p');
  version.className = 'credits-version';
  version.textContent = `Villopia ${APP_VERSION}`;

  creditsModal.content.append(list, version);
  const closeCredits = createButton({
    label: t('common.close'),
    variant: 'ghost',
    onClick: () => {
      creditsModal.close();
    },
  });
  creditsModal.actions.append(closeCredits.el);

  const creditsButton = createButton({
    label: t('settings.credits.open'),
    variant: 'secondary',
    onClick: () => {
      creditsModal.open();
    },
  });

  /* ── La remise à zéro ────────────────────────────────────────────────────── */

  const resetModal = createModal({
    title: t('settings.reset.title'),
    text: t('settings.reset.text'),
    icon: 'bolt',
  });
  document.body.append(resetModal.el);

  /*
    L'ordre des deux boutons n'est plus décidé ici.

    `createModal` donne le focus au premier élément focalisable : sur une action qui
    efface une ville de plusieurs heures, ce doit être le choix qui ne coûte rien — une
    confirmation destructrice pré-focalisée se valide d'un « Entrée » réflexe. Cet
    invariant vivait en commentaire, ici et dans `confirm.ts`. Il vit désormais dans
    `appendDecision`, qui pose les deux boutons et refuse de les inverser.
  */
  const cancelReset = createButton({
    label: t('common.cancel'),
    variant: 'ghost',
    onClick: () => {
      resetModal.close();
    },
  });

  const confirmReset = createButton({
    label: t('settings.reset.confirm'),
    variant: 'primary',
    onClick: () => {
      resetModal.close();
      options.onReset();
    },
  });

  appendDecision(resetModal, { cancel: cancelReset.el, confirm: confirmReset.el });

  const resetButton = createButton({
    label: t('settings.reset.open'),
    variant: 'ghost',
    icon: 'bolt',
    onClick: () => {
      resetModal.open();
    },
  });

  return {
    rows: [
      row('sound', t('settings.sound.effects'), sfx),
      row('credits', t('settings.credits.label'), creditsButton.el),
      row('reset', t('settings.reset.label'), resetButton.el),
    ],
    destroy() {
      creditsModal.destroy();
      creditsModal.el.remove();
      resetModal.destroy();
      resetModal.el.remove();
    },
  };
}
